import type { ExperienceDataMap, Gift } from "@/types";
import type { TemplateExperienceId } from "./templateExperiences";

// ─── Shapes específicos por experiência ────────────────────────────────────
// Os tipos abaixo descrevem o formato esperado dos dados personalizados de
// cada template extra. Todos os campos são opcionais para preservar
// compatibilidade com gifts antigos — quando ausentes, as seções publicadas
// caem para o fallback original (dados derivados do gift).

export interface TimelineEventData {
  date?: string;
  title?: string;
  description?: string;
  photoUrl?: string;
}

export interface TimelineExperienceData {
  title?: string;
  subtitle?: string;
  events?: TimelineEventData[];
}

export interface GalleryPhotoData {
  url?: string;
  caption?: string;
}

export interface GalleryExperienceData {
  title?: string;
  subtitle?: string;
  photos?: GalleryPhotoData[];
}

export interface CoupleWrappedExperienceData {
  startDate?: string;
  headline?: string;
  subtitle?: string;
  highlightMessage?: string;
}

export interface StarsMapExperienceData {
  specialDate?: string;
  locationLabel?: string;
  title?: string;
  message?: string;
  // Sempre representação artística — não promete cálculo astronômico real.
  isArtisticRepresentation?: boolean;
}

export type AchievementRarity = "common" | "rare" | "epic" | "legendary";
export type AchievementIcon = "heart" | "trophy" | "star" | "gift";

export interface AchievementItemData {
  title?: string;
  description?: string;
  rarity?: AchievementRarity;
  icon?: AchievementIcon;
  unlocked?: boolean;
}

export interface AchievementsExperienceData {
  title?: string;
  achievements?: AchievementItemData[];
}

export interface CuriosityItemData {
  question?: string;
  answer?: string;
}

export interface CuriositiesExperienceData {
  title?: string;
  items?: CuriosityItemData[];
}

export type GiftTreeIcon = "gift" | "heart" | "star" | "image" | "clock";

export interface GiftTreeItemData {
  title?: string;
  message?: string;
  icon?: GiftTreeIcon;
}

export interface GiftTreeExperienceData {
  title?: string;
  subtitle?: string;
  gifts?: GiftTreeItemData[];
}

export interface SurpriseWheelExperienceData {
  title?: string;
  question?: string;
  options?: string[];
  // Índice (na lista `options`) da opção que a roleta deve sempre cair.
  // `null` ou ausente = roleta aleatória (comportamento clássico). Permite
  // que quem cria o presente "trapaceie" pra garantir uma surpresa específica.
  riggedIndex?: number | null;
}

export interface MapJourneyStopData {
  title?: string;
  description?: string;
  date?: string;
  // Coordenadas reais do lugar marcado no mapa (Leaflet/OSM).
  // Opcionais pra manter back-compat com paradas simbólicas antigas.
  lat?: number;
  lng?: number;
  // Nome humano do lugar (ex.: "Praia de Copacabana, RJ") — vem da
  // busca Nominatim, mostrado como legenda do pin.
  locationName?: string;
  // URL da foto associada à parada — upload separado das `gift.photos`
  // pra não misturar com as fotos da galeria principal.
  photoUrl?: string;
}

export interface MapJourneyExperienceData {
  title?: string;
  subtitle?: string;
  stops?: MapJourneyStopData[];
}

export interface WordGameExperienceData {
  title?: string;
  hint?: string;
  secretWords?: string[];
}

// ─── Defaults por experiência ──────────────────────────────────────────────
// Sementes leves usadas para popular o formulário quando o usuário abre a
// personalização pela primeira vez. Não substituem o fallback elegante nas
// seções publicadas: aqui o objetivo é só dar um ponto de partida amigável.

export function buildDefaultExperienceData(
  id: string,
  gift: Partial<Gift>
): Record<string, unknown> {
  switch (id as TemplateExperienceId) {
    case "couple-wrapped": {
      const data: CoupleWrappedExperienceData = {
        startDate: gift.coupleStartDate ?? "",
        headline: "Nosso tempo juntos",
        subtitle: "Cada segundo dessa história merece ser lembrado.",
        highlightMessage: "",
      };
      return data as unknown as Record<string, unknown>;
    }
    case "timeline": {
      const data: TimelineExperienceData = {
        title: "Nossa linha do tempo",
        subtitle: "Os momentos que escreveram a nossa história.",
        events: [
          {
            date: "",
            title: "Nosso primeiro encontro",
            description: "O dia em que tudo começou.",
            photoUrl: "",
          },
        ],
      };
      return data as unknown as Record<string, unknown>;
    }
    case "gallery": {
      const data: GalleryExperienceData = {
        title: "Nossas memórias favoritas",
        subtitle: "Fotos que contam a nossa história.",
        photos: [],
      };
      return data as unknown as Record<string, unknown>;
    }
    case "stars-map": {
      const data: StarsMapExperienceData = {
        specialDate: gift.coupleStartDate ?? "",
        locationLabel: "",
        title: "O céu do nosso momento especial",
        message: "Uma representação artística do nosso céu.",
        isArtisticRepresentation: true,
      };
      return data as unknown as Record<string, unknown>;
    }
    case "achievements": {
      const data: AchievementsExperienceData = {
        title: "Nossas conquistas",
        achievements: [
          {
            title: "Primeiro Capítulo",
            description: "A nossa história começou.",
            rarity: "common",
            icon: "heart",
            unlocked: true,
          },
        ],
      };
      return data as unknown as Record<string, unknown>;
    }
    case "curiosities": {
      const data: CuriositiesExperienceData = {
        title: "Curiosidades sobre nós",
        items: [
          {
            question: "Quando essa história começou?",
            answer: "Em um dia que ficou marcado para sempre.",
          },
        ],
      };
      return data as unknown as Record<string, unknown>;
    }
    case "gift-tree": {
      const data: GiftTreeExperienceData = {
        title: "Presentes que contam essa história",
        subtitle: "Toque em cada presente para revelar um detalhe simbólico.",
        gifts: [
          {
            title: "Memórias",
            message: "Cada momento guardado virou parte deste presente.",
            icon: "heart",
          },
        ],
      };
      return data as unknown as Record<string, unknown>;
    }
    case "surprise-wheel": {
      const data: SurpriseWheelExperienceData = {
        title: "Uma surpresinha para nós",
        question: "Para onde vamos hoje?",
        options: ["Cinema", "Jantar", "Sushi", "Filme em casa"],
      };
      return data as unknown as Record<string, unknown>;
    }
    case "map-journey": {
      const data: MapJourneyExperienceData = {
        title: "Nossa Jornada no Mapa",
        subtitle: "Uma rota imaginária pelos momentos que vivemos juntos.",
        stops: [
          {
            title: "Onde tudo começou",
            description: "A primeira página dessa história.",
            date: gift.coupleStartDate ?? "",
          },
        ],
      };
      return data as unknown as Record<string, unknown>;
    }
    case "word-game": {
      const data: WordGameExperienceData = {
        title: "Desvende a palavra",
        hint: "Uma palavra curta que cabe em um suspiro.",
        secretWords: ["AMOR"],
      };
      return data as unknown as Record<string, unknown>;
    }
    default:
      return {};
  }
}

// ─── Acesso seguro aos dados de uma experiência ────────────────────────────
// As seções publicadas chamam essa função para extrair os dados personalizados
// do gift e cair no fallback elegante quando ausentes.

export function getExperienceData<T>(
  gift: Partial<Gift> | undefined,
  id: string
): Partial<T> | undefined {
  const map = gift?.experienceData;
  if (!map) return undefined;
  const value = map[id];
  if (!value || typeof value !== "object") return undefined;
  return value as Partial<T>;
}

// ─── Filtra dados apenas para experiências selecionadas ────────────────────
// Usado no checkout para garantir que não enviamos dados de experiências
// que o usuário acabou removendo.

export function filterExperienceData(
  data: ExperienceDataMap | undefined,
  selectedIds: string[]
): ExperienceDataMap {
  if (!data || selectedIds.length === 0) return {};
  const result: ExperienceDataMap = {};
  for (const id of selectedIds) {
    if (id in data) {
      result[id] = data[id];
    }
  }
  return result;
}

// ─── Normalização de palavras do word-game ─────────────────────────────────
// Remove acentos, normaliza para uppercase e filtra apenas letras. Usado no
// formulário e na seção publicada para manter a regra consistente.

export function normalizeWordForGame(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
}
